import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Autocomplete,
  Box,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontSize: "0.9rem",
    backgroundColor: "#fff",
    transition: "box-shadow 0.2s",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a0aec0" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    fontWeight: 500,
    color: "#64748b",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -8px) scale(0.75) !important",
    backgroundColor: "#fff",
    padding: "0 6px",
  },
};

const LOAD_MORE_THRESHOLD_PX = 160;

// ─── Isolated Autocomplete with its own local inputValue state ──────────────
// This prevents cross-field bleeding: each field owns its own text state.
const DropdownField = ({
  label,
  placeholder,
  value,
  options,
  loading,
  loadingMore,
  isReadOnly,
  onChange,
  onSearch,
  onSelect,
  onLoadMore,
  hasMore,
  focusSx,
  onOpenModal
}) => {
  // Local text state — only syncs from props when the selected *object* changes
  const [inputValue, setInputValue] = useState(() => {
    if (value && typeof value === "object") return value.label || value.name || "";
    if (typeof value === "string") return value;
    return "";
  });

  // Track previous value reference to detect external resets (e.g. location type change)
  const prevValueRef = useRef(value);
  useEffect(() => {
    const prev = prevValueRef.current;
    prevValueRef.current = value;

    // Only sync from props if the value changed externally (not from our own onChange)
    if (value !== prev) {
      if (value === null || value === undefined || value === "") {
        setInputValue("");
      } else if (value && typeof value === "object") {
        setInputValue(value.label || value.name || "");
      } else if (typeof value === "string" && value !== inputValue) {
        setInputValue(value);
      }
    }
  }, [value]);

  const searchDebounceRef = useRef(null);

  const selectedOption =
    value && typeof value === "object" && "id" in value ? value : null;

  return (
    <Autocomplete
      freeSolo={!onOpenModal}
      options={options}
      getOptionLabel={(opt) => {
        if (typeof opt === "string") return opt;
        return opt?.label || opt?.name || "";
      }}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newText, reason) => {
        // "reset" fires when user picks an option — don't overwrite with raw text
        if (reason === "reset") return;
        setInputValue(newText);
        if (onChange) onChange(newText);
        if (onSearch) {
          if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
          searchDebounceRef.current = setTimeout(() => {
            onSearch(newText);
          }, 400);
        }
      }}
      onChange={(_, newValue) => {
        // User selected an option from the list
        if (newValue && typeof newValue === "object") {
          if (newValue.recordStatusId === 3) {
            alert("This company is reported");
            return;
          }
          setInputValue(newValue.label || newValue.name || "");
        } else if (typeof newValue === "string") {
          setInputValue(newValue);
        } else {
          setInputValue("");
        }
        if (onSelect) onSelect(newValue);
      }}
      loading={loading}
      disabled={isReadOnly}
      PaperComponent={(paperProps) => (
        <Paper
          {...paperProps}
          sx={{
            ...paperProps.sx,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {paperProps.children}
        </Paper>
      )}
      ListboxProps={{
        onScroll: (e) => {
          const listboxNode = e.currentTarget;
          const remainingScroll =
            listboxNode.scrollHeight -
            listboxNode.scrollTop -
            listboxNode.clientHeight;
          if (
            remainingScroll <= LOAD_MORE_THRESHOLD_PX &&
            typeof onLoadMore === "function" &&
            !loading &&
            !loadingMore &&
            hasMore
          ) {
            onLoadMore();
          }
        },
      }}
      getOptionDisabled={(opt) => opt?.recordStatusId === 3}
      renderOption={(renderProps, opt) => {
        const { key, ...optionProps } = renderProps;
        const isReported = opt?.recordStatusId === 3;
        const isManufacturer = !!opt?.manufacturer;
        const isVerified = Boolean(opt?.verified || opt?.isVerified || opt?.isCompanyVerified);

        return (
          <li key={key} {...optionProps} style={{ opacity: isReported ? 0.5 : 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>{typeof opt === "string" ? opt : opt?.label || opt?.name}</span>
                {isVerified && (
                  <Tooltip title="This company has been verified" arrow>
                    <VerifiedIcon sx={{ color: "#083DB8", fontSize: "1rem" }} />
                  </Tooltip>
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {isManufacturer && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      bgcolor: "error.light",
                      color: "error.contrastText",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1
                    }}
                  >
                    Manufacturer
                  </Box>
                )}
                {isReported && (
                  <Tooltip title={isManufacturer ? "This manufacturer is reported" : "This company is reported"} arrow>
                    <IconButton
                      size="small"
                      sx={{ color: "error.main" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        alert(isManufacturer ? "This manufacturer is reported" : "This company is reported");
                      }}
                    >
                      <WarningIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </li>
        );
      }}
      isOptionEqualToValue={(opt, val) =>
        opt?.id !== undefined && val?.id !== undefined
          ? opt.id === val.id
          : opt === val
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="medium"
          fullWidth
          onClick={onOpenModal ? onOpenModal : undefined}
          sx={{
            ...focusSx,
            cursor: onOpenModal ? "pointer" : "default"
          }}
          InputProps={{
            ...params.InputProps,
            readOnly: !!onOpenModal,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps?.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const DynamicFormField = ({
  field,
  value,
  onChange,
  options = [],
  loading = false,
  loadingMore = false,
  onSearch,
  onSelect,
  onLoadMore,
  hasMore = false,
  accentColor,
  uploadedImages = [],
  onImageAdd,
  onImageRemove,
  onOpenModal,
}) => {
  if (!field || field.isVisible === false) return null;

  const isRequired = !!field.isRequired;
  const isReadOnly = !!field.isReadOnly;
  const label = `${field.fieldName}${isRequired ? " *" : ""}`;
  const placeholder = field.placeholder || "";
  const accent = accentColor || "#6366f1";

  const focusSx = {
    ...fieldSx,
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: `0 0 0 3px ${accent}22`,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: accent },
  };

  switch (field.fieldType) {
    case "Text":
    case "Number":
      return (
        <TextField
          label={label}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          type={field.fieldType === "Number" ? "number" : "text"}
          fullWidth
          size="medium"
          disabled={isReadOnly}
          sx={focusSx}
        />
      );

    case "MultilineText":
      return (
        <TextField
          label={label}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          multiline
          rows={4}
          fullWidth
          size="medium"
          disabled={isReadOnly}
          sx={focusSx}
        />
      );

    case "Date": {
      let formattedDate = "";
      if (value) {
        if (typeof value === "string") {
          formattedDate = value.split("T")[0];
        } else if (value instanceof Date) {
          formattedDate = value.toISOString().split("T")[0];
        } else {
          formattedDate = String(value);
        }
      }
      return (
        <TextField
          label={label}
          value={formattedDate}
          onChange={(e) => onChange(e.target.value)}
          type="date"
          fullWidth
          size="medium"
          disabled={isReadOnly}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...focusSx,
            "& .MuiInputLabel-root": {
              transform: "translate(14px, -8px) scale(0.75) !important",
              backgroundColor: "#fff",
              padding: "0 6px",
            },
          }}
        />
      );
    }

    case "Scanner":
      return (
        <TextField
          label={label}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Scan or type barcode…"}
          fullWidth
          size="medium"
          disabled={isReadOnly}
          sx={focusSx}
          InputProps={{
            endAdornment: (
              <QrCode2Icon sx={{ color: accent, fontSize: 22, opacity: 0.8 }} />
            ),
          }}
        />
      );

    case "Dropdown":
      return (
        <DropdownField
          label={label}
          placeholder={placeholder}
          value={value}
          options={options}
          loading={loading}
          loadingMore={loadingMore}
          isReadOnly={isReadOnly}
          onChange={onChange}
          onSearch={onSearch}
          onSelect={onSelect}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          focusSx={focusSx}
          onOpenModal={onOpenModal}
        />
      );

    case "ImageUpload":
      return (
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: "#374151",
              fontSize: "0.85rem",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <AddPhotoAlternateIcon sx={{ fontSize: 16, color: accent }} />
            {label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              p: 2,
              border: "1.5px dashed #e2e8f0",
              borderRadius: "12px",
              bgcolor: "#f8fafc",
              minHeight: 120,
              alignItems: "flex-start",
            }}
          >
            <Paper
              variant="outlined"
              component="label"
              sx={{
                width: 110,
                height: 110,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderStyle: "dashed",
                borderColor: accent,
                borderWidth: "1.5px",
                bgcolor: `${accent}08`,
                borderRadius: "10px",
                transition: "all 0.2s",
                "&:hover": { bgcolor: `${accent}14`, transform: "scale(1.02)" },
              }}
            >
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={onImageAdd}
              />
              <CloudUploadIcon sx={{ color: accent, fontSize: 28 }} />
              <Typography
                variant="caption"
                sx={{ mt: 0.5, color: accent, fontWeight: 600, fontSize: "0.72rem" }}
              >
                Upload Photos
              </Typography>
            </Paper>

            {uploadedImages.map((img, i) => (
              <Box
                key={i}
                sx={{
                  position: "relative",
                  width: 110,
                  height: 110,
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1.5px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  transition: "transform 0.15s",
                  "&:hover": { transform: "scale(1.03)" },
                }}
              >
                <img
                  src={img.preview || URL.createObjectURL(img.file)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  alt={`upload-${i}`}
                />
                <IconButton
                  size="small"
                  onClick={() => onImageRemove(i)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "rgba(255,255,255,0.92)",
                    color: "#ef4444",
                    width: 22,
                    height: 22,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 28,
                    background: "linear-gradient(to top, rgba(0,0,0,0.4), transparent)",
                  }}
                />
              </Box>
            ))}

            {uploadedImages.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#94a3b8",
                  fontSize: "0.8rem",
                  ml: 1,
                  alignSelf: "center",
                }}
              >
                No images added yet
              </Box>
            )}
          </Box>
          {uploadedImages.length > 0 && (
            <Typography
              variant="caption"
              sx={{ mt: 0.5, color: "#64748b", display: "block" }}
            >
              {uploadedImages.length} photo
              {uploadedImages.length !== 1 ? "s" : ""} selected
            </Typography>
          )}
        </Box>
      );

    default:
      return null;
  }
};

export default DynamicFormField;